import React, { useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../../../helpers/axiosInstance";
import useAuth from "../../../../hooks/useAuth";
import { date, dateTime, fullName, money } from "../../../../helpers/format";
import { Async, Banner, Button, Card, Confirm, DownloadButton, Empty, Field, Page, Pill, SectionTitle, useAction, useFetch } from "../../../../shared/ui/ui";

// One record in full, as the app shows it, each with its PDF where the fund
// prints one. The console listed these and stopped there: a row was as far as
// you could go.

const receiptUrl = (organisationId, kind, id) => `/api/receipt/${organisationId}/${kind}/${id}/pdf`;

const memberName = (record) => (record?.member && typeof record.member === "object" ? fullName(record.member) : null);

// ---------------------------------------------------------------- invoices

const amountDue = (invoice) => Math.max(0, (Number(invoice.total_amount) || 0) - (Number(invoice.total_payments) || 0));
const invoiceStatus = (invoice) => {
  if (invoice.status === "paid" || amountDue(invoice) <= 0) return ["good", "Paid"];
  if (invoice.due_date && Date.now() > Number(invoice.due_date)) return ["bad", "Overdue"];
  if (invoice.status === "partial") return ["warn", "Partly paid"];
  return ["warn", "Unpaid"];
};
const lineTotal = (line) => (Number(line.item_price) || 0) * (Number(line.item_quantity) || 1) + (Number(line.item_interest) || 0);

/** One invoice, its lines, its receipt, and the way to take its payment. */
export const InvoiceDetail = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const { organisation, can } = useAuth();
  const invoice = useFetch(`/api/invoice/${organisation._id}/invoices/${invoiceId}`);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { busy, run } = useAction();

  // The server refuses an invoice with a payment or a late penalty against it,
  // and says why.
  const remove = async () => {
    const ok = await run(() => axiosInstance.delete(`/api/invoice/${organisation._id}/invoices/${invoiceId}`), {
      done: (data) => data.message || "Invoice deleted",
    });
    if (ok) navigate("/admin/invoices", { replace: true });
  };

  return (
    <Async state={invoice}>
      {(data) => {
        if (!data?._id) {
          return (
            <Page title="Invoice" back>
              <Empty title="Invoice not found" />
            </Page>
          );
        }
        const [tone, label] = invoiceStatus(data);
        const due = amountDue(data);
        // Only an invoice nothing has been paid against, as in the app:
        // anything else has already moved money, which deleting would not put back.
        const mayDelete = can("invoices", "delete") && data.status === "unpaid" && !Number(data.total_payments) && !data.late_penalty_invoice;
        return (
          <Page title={data.invoice_number || "Invoice"} subtitle={memberName(data)} back>
            <Card>
              <div className="sf-row" style={{ padding: 0, border: 0 }}>
                <div className="sf-row-main">
                  <p className="sf-stat-label">{due > 0 ? "Still to pay" : "Total"}</p>
                  <p className="sf-stat-value">{money(due > 0 ? due : data.total_amount)}</p>
                </div>
                <Pill tone={tone}>{label}</Pill>
              </div>
              <dl className="sf-kv" style={{ marginTop: "1rem" }}>
                <dt>Raised</dt>
                <dd>{date(data.date)}</dd>
                <dt>Due</dt>
                <dd>{date(data.due_date)}</dd>
                <dt>Total</dt>
                <dd>{money(data.total_amount)}</dd>
                <dt>Paid so far</dt>
                <dd>{money(data.total_payments)}</dd>
                {data.payment_date && label === "Paid" ? (
                  <>
                    <dt>Paid on</dt>
                    <dd>{date(data.payment_date)}</dd>
                  </>
                ) : null}
                {data.notes ? (
                  <>
                    <dt>Notes</dt>
                    <dd>{data.notes}</dd>
                  </>
                ) : null}
              </dl>
            </Card>
            <SectionTitle title="What it is for" />
            <Card flush>
              {(data.items || []).length ? (
                data.items.map((line) => (
                  <div key={line._id} className="sf-row">
                    <div className="sf-row-main">
                      <p className="sf-row-title">{line.item_name}</p>
                      <p className="sf-row-sub">
                        {line.item_description}
                        {Number(line.item_interest) ? ` · includes ${money(line.item_interest)} interest` : ""}
                      </p>
                    </div>
                    <p className="sf-row-title sf-row-end">{money(lineTotal(line))}</p>
                  </div>
                ))
              ) : (
                <div className="sf-row sf-muted">This invoice has no lines.</div>
              )}
            </Card>
            <div style={{ display: "grid", gap: "0.75rem", marginTop: "1rem" }}>
              <DownloadButton block label="Download receipt" fileName={`${data.invoice_number || "invoice"}.pdf`} url={`/api/invoice/${organisation._id}/invoices/${data._id}/receipt`} />
              {due > 0 && can("payments", "add") ? (
                <Button variant="primary" block onClick={() => navigate(`/admin/take-payment?member=${data.userId || data.member?._id}`)}>
                  Take payment of {money(due)}
                </Button>
              ) : null}
              {mayDelete ? (
                <Button variant="danger" block onClick={() => setConfirmDelete(true)}>
                  Delete this invoice
                </Button>
              ) : null}
            </div>
            {confirmDelete ? (
              <Confirm title={`Delete ${data.invoice_number || "this invoice"}?`} confirmLabel="Delete" danger busy={busy} onCancel={() => setConfirmDelete(false)} onConfirm={remove}>
                {`The invoice${memberName(data) ? ` for ${memberName(data)}` : ""}, ${money(data.total_amount)}, and its lines are removed for good. This cannot be undone.`}
              </Confirm>
            ) : null}
          </Page>
        );
      }}
    </Async>
  );
};

// ---------------------------------------------------------------- payments

/** Payments, newest first: everyone's for a role that sees them, else your own. */
export const PaymentsList = () => {
  const navigate = useNavigate();
  const { organisation, auth, seesEveryone } = useAuth();
  const everyone = seesEveryone("payments");
  const [method, setMethod] = useState("all");
  const query = method === "all" ? "" : `?method=${method}`;
  const payments = useFetch(
    everyone ? `/api/payment/${organisation._id}/getallpayments${query}` : `/api/payment/${organisation._id}/members/${auth.user._id}/payments${query}`,
    { select: (body) => body.data || [] }
  );

  return (
    <Page title="Payments" subtitle={everyone ? "Every payment made to the fund." : "What you have paid the fund."}>
      <div className="sf-filter-bar">
        <select value={method} onChange={(event) => setMethod(event.target.value)} aria-label="Method">
          <option value="all">Every method</option>
          {["CASH", "UPI", "BANK", "CHEQUE"].map((option) => (
            <option key={option}>{option}</option>
          ))}
        </select>
      </div>
      <Async state={payments}>
        {(list) =>
          !list.length ? (
            <Empty title="No payments">Payments appear here once they are recorded.</Empty>
          ) : (
            <Card flush>
              {list.map((payment) => (
                <button key={payment._id} className="sf-row sf-row-button" onClick={() => navigate(`/admin/payments/${payment._id}`)}>
                  <div className="sf-row-main">
                    <p className="sf-row-title">{everyone ? memberName(payment) : payment.notes || "Payment"}</p>
                    <p className="sf-row-sub">
                      {date(payment.payment_date || payment.date)} · {payment.method}
                    </p>
                  </div>
                  <div className="sf-row-end">
                    <p className="sf-row-title">{money(payment.total_amount)}</p>
                    <Pill tone={Number(payment.status) === 0 ? "good" : "warn"}>{Number(payment.status) === 0 ? "Applied" : "Processing"}</Pill>
                  </div>
                </button>
              ))}
            </Card>
          )
        }
      </Async>
    </Page>
  );
};

/**
 * One payment in full. Deleting it undoes what it was spread across —
 * savings, repayments and vouchers — and reopens its invoice.
 */
export const PaymentDetail = () => {
  const { paymentId } = useParams();
  const navigate = useNavigate();
  const { organisation, can } = useAuth();
  const payment = useFetch(`/api/payment/${organisation._id}/payments/${paymentId}`, { select: (body) => body.data });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { busy, run } = useAction();

  // The server undoes everything the payment recorded and reopens its invoice.
  const remove = async () => {
    const ok = await run(() => axiosInstance.delete(`/api/payment/${organisation._id}/payments/${paymentId}`), {
      done: (data) => data.message || "Payment deleted",
    });
    if (ok) navigate("/admin/payments", { replace: true });
  };

  return (
    <Async state={payment}>
      {(data) =>
        !data ? (
          <Page title="Payment" back>
            <Empty title="Payment not found" />
          </Page>
        ) : (
          <Page title={money(data.total_amount)} subtitle={memberName(data)} back>
            <Card>
              <dl className="sf-kv">
                <dt>Paid on</dt>
                <dd>{dateTime(data.payment_date || data.date)}</dd>
                <dt>Method</dt>
                <dd>{data.method}</dd>
                <dt>Status</dt>
                <dd>
                  <Pill tone={Number(data.status) === 0 ? "good" : "warn"}>{Number(data.status) === 0 ? "Applied" : "Processing"}</Pill>
                </dd>
                {data.notes ? (
                  <>
                    <dt>Notes</dt>
                    <dd>{data.notes}</dd>
                  </>
                ) : null}
              </dl>
            </Card>
            {Number(data.status) !== 0 ? (
              <Banner tone="warn">It is still being spread across savings, repayments and charges. That usually takes a moment.</Banner>
            ) : null}
            <DownloadButton block label="Download receipt" fileName={`payment-${data._id}.pdf`} url={receiptUrl(organisation._id, "payment", data._id)} />
            {can("payments", "delete") ? (
              <div style={{ marginTop: "1rem" }}>
                <Button variant="danger" block onClick={() => setConfirmDelete(true)}>
                  Delete this payment
                </Button>
              </div>
            ) : null}
            {confirmDelete ? (
              <Confirm title="Delete this payment?" confirmLabel="Delete" danger busy={busy} onCancel={() => setConfirmDelete(false)} onConfirm={remove}>
                {`The ${money(data.total_amount)} payment from ${memberName(data)} is removed, along with everything it recorded: the savings it credited, any loan repayment and interest, and its vouchers. The invoice it paid is reopened. This cannot be undone.`}
              </Confirm>
            ) : null}
          </Page>
        )
      }
    </Async>
  );
};

// ---------------------------------------------------------------- loans

const round2 = (value) => Math.round(value * 100) / 100;
const roundUpIfFractional = (value) => (Number.isInteger(value) ? value : Math.ceil(value));

/**
 * What a loan's interest really comes to. A reducing-balance loan's stored
 * interest is a flat estimate written at application time; the fund charges
 * the loan's monthly rate (1.3% for loans made before funds set their own) of
 * the falling balance, so this works it out the way the server bills it — the
 * app does the same.
 */
const monthlyRateOf = (loan) => Number(loan.monthly_interest_rate) || 1.3;

const expectedInterest = (loan) => {
  if (!loan.prorated_interest || !(loan.term > 0) || !(loan.amount > 0)) return Number(loan.interest) || 0;
  const monthlyPrincipal = roundUpIfFractional(round2(loan.amount / loan.term));
  let outstanding = loan.amount;
  const rate = monthlyRateOf(loan) / 100;
  let total = 0;
  for (let month = 0; month < loan.term; month++) {
    total += roundUpIfFractional(round2(outstanding * rate));
    outstanding = round2(outstanding - monthlyPrincipal);
  }
  return total;
};

/** One loan, with every figure worked out from its repayments. */
export const LoanDetail = () => {
  const { loanId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { organisation, auth, can, seesEveryone } = useAuth();
  const fromList = location.state?.loan;
  const loans = useFetch(
    seesEveryone("loans")
      ? `/api/loans/${organisation._id}/getalltransactions?member_id=all&active=all&startdate=0`
      : `/api/loans/${organisation._id}/gettransactionbyid/${auth.user._id}?active=all&startdate=0`,
    { skip: Boolean(fromList), select: (body) => body.data || [] }
  );
  const repayments = useFetch(`/api/repayment/${organisation._id}/getbyloan/${loanId}`, { select: (body) => body.data || [] });
  const [schedule, setSchedule] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const [active, setActive] = useState(null);
  const { busy, run } = useAction();

  const loan = fromList || loans.data?.find((candidate) => candidate._id === loanId);

  const progress = useMemo(() => {
    const rows = repayments.data || [];
    const principal = rows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
    const interest = rows.reduce((sum, row) => sum + (Number(row.interest) || 0), 0);
    return { principal, interest, paid: principal + interest, count: rows.length };
  }, [repayments.data]);

  if (!loan) {
    return loans.loading ? (
      <Page title="Loan" back />
    ) : (
      <Page title="Loan" back>
        <Empty title="Loan not found">Open it again from Loans.</Empty>
      </Page>
    );
  }

  const isActive = active ?? loan.active;
  const interest = expectedInterest(loan);
  const expectedTotal = (Number(loan.amount) || 0) + interest;
  const pending = Math.max(0, expectedTotal - progress.paid);
  const fraction = expectedTotal > 0 ? Math.min(1, progress.paid / expectedTotal) : 0;

  const showSchedule = async () => {
    const body = await run(() => axiosInstance.post(`/api/applyloan/${organisation._id}/calculate-emi`, { amount: loan.amount, term: loan.term }));
    if (body?.data) setSchedule(body.data.emi_chart || []);
  };
  const toggleActive = async () => {
    const ok = await run(() => axiosInstance.post(`/api/loans/${organisation._id}/changeloanstatus/${loan._id}`, { active: !isActive }), {
      done: isActive ? "Loan closed" : "Loan reopened",
    });
    if (ok) {
      setActive(!isActive);
      setConfirming(null);
    }
  };
  const remove = async () => {
    const ok = await run(() => axiosInstance.delete(`/api/loans/${organisation._id}/deletetransaction/${loan._id}`), { done: "Loan deleted" });
    if (ok) navigate("/admin/loans");
  };

  return (
    <Page title={money(loan.amount)} subtitle={memberName(loan)} back>
      <Card>
        <div className="sf-row" style={{ padding: 0, border: 0 }}>
          <div className="sf-row-main">
            <p className="sf-stat-label">Still to pay</p>
            <p className="sf-stat-value">{money(pending)}</p>
          </div>
          <Pill tone={isActive ? "warn" : "good"}>{isActive ? "Active" : "Closed"}</Pill>
        </div>
        <div className="sf-progress" aria-label="Repaid">
          <span style={{ width: `${Math.round(fraction * 100)}%` }} />
        </div>
        <p className="sf-muted" style={{ margin: 0 }}>
          {money(progress.paid)} repaid of {money(expectedTotal)} · {progress.count} of {loan.term} instalments
        </p>
      </Card>
      <Card>
        <dl className="sf-kv">
          <dt>Borrowed</dt>
          <dd>{money(loan.amount)}</dd>
          <dt>Interest over the term</dt>
          <dd>{money(interest)}</dd>
          <dt>How interest is worked out</dt>
          <dd>{loan.prorated_interest ? `Reducing balance: ${monthlyRateOf(loan)}% a month of what is still owed` : "Fixed for the whole term"}</dd>
          <dt>Principal repaid</dt>
          <dd>{money(progress.principal)}</dd>
          <dt>Interest paid</dt>
          <dd>{money(progress.interest)}</dd>
          <dt>Term</dt>
          <dd>{loan.term} months</dd>
          <dt>Sanctioned</dt>
          <dd>{date(loan.sanction_date || loan.date)}</dd>
          <dt>Ends</dt>
          <dd>{date(loan.end_date)}</dd>
          {loan.comment ? (
            <>
              <dt>Purpose</dt>
              <dd>{loan.comment}</dd>
            </>
          ) : null}
        </dl>
      </Card>

      <div className="sf-chips" style={{ margin: "0.5rem 0 1rem" }}>
        <DownloadButton label="Loan statement" fileName={`loan-${loan._id}.pdf`} url={receiptUrl(organisation._id, "loan", loan._id)} />
        <Button onClick={schedule ? () => setSchedule(null) : showSchedule} busy={busy && !schedule && !confirming}>
          {schedule ? "Hide EMI schedule" : "EMI schedule"}
        </Button>
        {can("loans", "edit") ? <Button onClick={() => setConfirming("status")}>{isActive ? "Close loan" : "Reopen loan"}</Button> : null}
        {can("loans", "delete") ? (
          <Button variant="danger" onClick={() => setConfirming("delete")}>
            Delete
          </Button>
        ) : null}
      </div>

      {schedule ? (
        <Card flush>
          <div style={{ overflowX: "auto" }}>
            <table className="sf-table">
              <thead>
                <tr>
                  <th>Month</th>
                  <th className="sf-num">EMI</th>
                  <th className="sf-num">Principal</th>
                  <th className="sf-num">Interest</th>
                  <th className="sf-num">Left to pay</th>
                </tr>
              </thead>
              <tbody>
                {schedule.map((row) => (
                  <tr key={row.month}>
                    <td>{row.month}</td>
                    <td className="sf-num">{money(row.emi)}</td>
                    <td className="sf-num">{money(row.principal)}</td>
                    <td className="sf-num">{money(row.interest)}</td>
                    <td className="sf-num">{money(row.remaining_principal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : null}

      <SectionTitle title="Repayments" />
      <Async state={repayments}>
        {(rows) =>
          !rows.length ? (
            <Empty title="No repayments yet" />
          ) : (
            <Card flush>
              {rows.map((row) => (
                <div key={row._id} className="sf-row">
                  <div className="sf-row-main">
                    <p className="sf-row-title">{date(row.date)}</p>
                    <p className="sf-row-sub">{row.comment || `Instalment ${row.term} of ${row.total_term || loan.term}`}</p>
                  </div>
                  <div className="sf-row-end">
                    <p className="sf-row-title">{money(row.amount)}</p>
                    {Number(row.interest) ? <p className="sf-row-sub">+ {money(row.interest)} interest</p> : null}
                  </div>
                </div>
              ))}
            </Card>
          )
        }
      </Async>

      {confirming === "status" ? (
        <Confirm title={isActive ? "Close this loan?" : "Reopen this loan?"} confirmLabel={isActive ? "Close loan" : "Reopen loan"} busy={busy} onCancel={() => setConfirming(null)} onConfirm={toggleActive}>
          {isActive ? "A closed loan no longer counts towards what the member owes." : "It counts towards what the member owes again."}
        </Confirm>
      ) : null}
      {confirming === "delete" ? (
        <Confirm title="Delete this loan?" confirmLabel="Delete" danger busy={busy} onCancel={() => setConfirming(null)} onConfirm={remove}>
          It comes out of the fund's records and out of what the member owes. This cannot be undone.
        </Confirm>
      ) : null}
    </Page>
  );
};

// ---------------------------------------------------------------- savings, interest, vouchers

const RECORD_KINDS = {
  savings: { title: "Savings entry", feature: "savings", receipt: "savings", remove: (org, id) => `/api/savings/${org}/deletetransaction/${id}`, back: "/admin/save" },
  interest: { title: "Interest record", feature: "interest_records", receipt: "interest", remove: (org, id) => `/api/interest/${org}/deleteinterest/${id}`, back: "/admin/interestrecord" },
  voucher: { title: "Voucher", feature: "vouchers", receipt: "voucher", remove: (org, id) => `/api/voucher/${org}/deletevoucher/${id}`, back: "/admin/vouchers" },
};

/**
 * One savings entry, interest record or voucher, with its PDF. Opened from
 * its list, which hands the record over — there is no endpoint for one.
 */
export const RecordDetail = () => {
  const { kind, recordId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { organisation, can } = useAuth();
  const [confirming, setConfirming] = useState(false);
  const { busy, run } = useAction();
  const config = RECORD_KINDS[kind];
  const record = location.state?.record;

  if (!config || !record || record._id !== recordId) {
    return (
      <Page title="Record" back>
        <Empty title="Open it from its list">This record can only be opened from the list it is in.</Empty>
      </Page>
    );
  }
  const credit = record.type === "credit";
  const remove = async () => {
    const ok = await run(() => axiosInstance.delete(config.remove(organisation._id, record._id)), { done: `${config.title} deleted` });
    if (ok) navigate(config.back);
  };

  return (
    <Page title={config.title} subtitle={memberName(record)} back>
      <Card>
        <p className="sf-stat-label">{credit ? "In" : "Out"}</p>
        <p className="sf-stat-value">
          {credit ? "+" : "−"}
          {money(record.amount)}
        </p>
        <dl className="sf-kv" style={{ marginTop: "1rem" }}>
          <dt>Date</dt>
          <dd>{date(record.date)}</dd>
          {record.category ? (
            <>
              <dt>Category</dt>
              <dd>{record.category}</dd>
            </>
          ) : null}
          {record.transfer_type ? (
            <>
              <dt>Paid by</dt>
              <dd>{record.transfer_type}</dd>
            </>
          ) : null}
          {kind === "savings" && record.total_amount != null ? (
            <>
              <dt>Balance after</dt>
              <dd>{money(record.total_amount)}</dd>
            </>
          ) : null}
          {record.comment || record.remarks ? (
            <>
              <dt>Remarks</dt>
              <dd>{record.comment || record.remarks}</dd>
            </>
          ) : null}
        </dl>
      </Card>
      <div style={{ display: "grid", gap: "0.75rem" }}>
        <DownloadButton block label="Download receipt" fileName={`${kind}-${record._id}.pdf`} url={receiptUrl(organisation._id, config.receipt, record._id)} />
        {/* A deposit's interest goes with the deposit; the server refuses to delete it on its own. */}
        {record.fd_id || record.rd_id ? (
          <>
            <Button
              block
              onClick={() => navigate(record.fd_id ? `/admin/fixed-deposits/${record.fd_id}` : `/admin/recurring-deposits/${record.rd_id}`)}
            >
              Open the {record.fd_id ? "FD" : "RD"} and each member's share
            </Button>
            <p className="sf-muted">
              This is a {record.fd_id ? "fixed" : "recurring"} deposit's interest. To remove it, reopen the {record.fd_id ? "FD" : "RD"}.
            </p>
          </>
        ) : can(config.feature, "delete") ? (
          <Button variant="danger" block onClick={() => setConfirming(true)}>
            Delete
          </Button>
        ) : null}
      </div>
      {confirming ? (
        <Confirm title={`Delete this ${config.title.toLowerCase()}?`} confirmLabel="Delete" danger busy={busy} onCancel={() => setConfirming(false)} onConfirm={remove}>
          {kind === "voucher"
            ? "The voucher comes out of the fund's records, but anything it took from members' savings stays taken — correct that with a savings entry. This cannot be undone."
            : "It comes out of the fund's records and every balance worked out from it. This cannot be undone."}
        </Confirm>
      ) : null}
    </Page>
  );
};

// ---------------------------------------------------------------- loan applications

/** `approved_members` arrives as a JSON string holding an array of ids. */
const approvedIds = (form) => {
  try {
    const value = typeof form.approved_members === "string" ? JSON.parse(form.approved_members) : form.approved_members;
    return Array.isArray(value) ? value.map((entry) => String(entry?._id || entry)) : [];
  } catch (_) {
    return [];
  }
};

// The codes the server keeps. There is no "rejected": taking an approval back
// drops the application to 0 or 1 again.
const FORM_STATUS = {
  0: ["warn", "Awaiting approvers"],
  1: ["warn", "Awaiting sanction"],
  2: ["good", "Sanctioned"],
};

/**
 * One loan application and what can be done about it. Two gates stand
 * between it and the money, and they belong to different people: the
 * approvers the fund chose approve it, then a sanctioner signs it off.
 * Whoever is looking gets only the action that is theirs.
 */
export const LoanApplicationDetail = () => {
  const { formId } = useParams();
  const navigate = useNavigate();
  const { organisation, auth, access, can, isSuperAdmin } = useAuth();
  const forms = useFetch(`/api/applyloan/${organisation._id}/getloansform`, { select: (body) => body.data || [] });
  const approvers = useFetch(`/api/applyloan/${organisation._id}/getloanapprovers/${formId}`, { select: (body) => body.data });
  const [confirming, setConfirming] = useState(null);
  const { busy, run } = useAction();

  return (
    <Async state={forms}>
      {(list) => {
        const form = list.find((candidate) => candidate._id === formId);
        if (!form) {
          return (
            <Page title="Loan application" back>
              <Empty title="Application not found">It may have been withdrawn.</Empty>
            </Page>
          );
        }
        const me = auth.user._id;
        const mine = (form.member?._id || form.userId) === me;
        const roster = approvers.data;
        const finished = form.status === 2;
        const approved = approvedIds(form).includes(String(me));
        const mayApprove = can("loan_applications", "approve");
        const maySanction = isSuperAdmin || access.loan_sanctioner;
        const mayDelete = can("loan_applications", "delete");
        const everyoneIn = Boolean(roster?.all_mandatory_approved);
        const waiting = (roster?.mandatory || []).filter((person) => !person.approved).map((person) => person.name);
        const [tone, label] = FORM_STATUS[form.status] || FORM_STATUS[0];

        const act = async (kind) => {
          const urls = {
            approve: () => axiosInstance.post(`/api/applyloan/${organisation._id}/approveloanform/${me}/loanform/${form._id}`),
            unapprove: () => axiosInstance.post(`/api/applyloan/${organisation._id}/rejectloanform/${me}/loanform/${form._id}`),
            sanction: () => axiosInstance.post(`/api/applyloan/${organisation._id}/approveloanform/${form.member?._id || form.userId}/admin/${form._id}`),
            remove: () => axiosInstance.delete(`/api/applyloan/${organisation._id}/deleteloanform/${form._id}`),
          };
          const done = { approve: "Approved", unapprove: "Your approval is withdrawn", sanction: "Loan sanctioned", remove: "Application deleted" }[kind];
          const ok = await run(urls[kind], { done });
          if (!ok) return;
          setConfirming(null);
          if (kind === "remove") navigate("/admin/applyloans");
          else {
            forms.reload();
            approvers.reload();
          }
        };

        const Person = ({ person, sanctioner }) => (
          <div className="sf-row">
            <p className="sf-row-main sf-row-title" style={{ margin: 0 }}>
              {person.name}
              {person.id === me ? " (you)" : ""}
            </p>
            {sanctioner ? null : <Pill tone={person.approved ? "good" : ""}>{person.approved ? "Approved" : "Waiting"}</Pill>}
          </div>
        );

        return (
          <Page title={money(form.amount)} subtitle={memberName(form)} back>
            <Card>
              <div className="sf-row" style={{ padding: 0, border: 0 }}>
                <div className="sf-row-main">
                  <p className="sf-stat-label">Monthly EMI</p>
                  <p className="sf-stat-value">{money(form.monthly_emi)}</p>
                </div>
                <Pill tone={tone}>{label}</Pill>
              </div>
              <dl className="sf-kv" style={{ marginTop: "1rem" }}>
                <dt>Amount</dt>
                <dd>{money(form.amount)}</dd>
                <dt>Interest</dt>
                <dd>{money(form.interest)}</dd>
                <dt>Total to repay</dt>
                <dd>{money(form.total_amount)}</dd>
                <dt>Term</dt>
                <dd>{form.term} months</dd>
                <dt>Applied</dt>
                <dd>{date(form.created_date)}</dd>
                {form.comment ? (
                  <>
                    <dt>Purpose</dt>
                    <dd>{form.comment}</dd>
                  </>
                ) : null}
              </dl>
            </Card>

            {roster ? (
              <>
                <SectionTitle title="Must approve" />
                <Card flush>
                  {roster.mandatory.length ? roster.mandatory.map((person) => <Person key={person.id} person={person} />) : <div className="sf-row sf-muted">Nobody is required to approve it.</div>}
                </Card>
                {roster.optional.length ? (
                  <>
                    <SectionTitle title="May approve" />
                    <Card flush>
                      {roster.optional.map((person) => (
                        <Person key={person.id} person={person} />
                      ))}
                    </Card>
                  </>
                ) : null}
                <SectionTitle title="May sanction" />
                <Card flush>
                  {roster.sanctioners.length ? roster.sanctioners.map((person) => <Person key={person.id} person={person} sanctioner />) : <div className="sf-row sf-muted">Only the SwitchFund team can sanction it.</div>}
                </Card>

              </>
            ) : null}

            <div style={{ display: "grid", gap: "0.75rem", marginTop: "1rem" }}>
              {mine ? (
                <Banner>
                  {finished
                    ? "Your application has been sanctioned."
                    : "This is your application. The mandatory approvers approve it, then a sanctioner sanctions it."}
                </Banner>
              ) : finished ? (
                <>
                  <Banner tone="good">This application has been sanctioned and the loan is made. There is nothing left to approve.</Banner>
                  {mayDelete ? (
                    <Button variant="danger" block onClick={() => setConfirming("remove")}>
                      Delete this application
                    </Button>
                  ) : null}
                </>
              ) : mayApprove || maySanction || mayDelete ? (
                <>
                  <SectionTitle title="Your decision" />
                  {mayApprove ? (
                    <>
                      {approved ? (
                        <Button block onClick={() => setConfirming("unapprove")}>
                          ↶ Withdraw my approval
                        </Button>
                      ) : (
                        <Button variant="primary" block onClick={() => setConfirming("approve")}>
                          Approve this application
                        </Button>
                      )}
                      <p className="sf-muted" style={{ margin: 0 }}>
                        {approved ? "You have approved this application." : "Approving says you are happy for the fund to lend this."}
                      </p>
                    </>
                  ) : null}
                  {maySanction ? (
                    <>
                      {/* Only once every mandatory approver is in: the fund refuses it before then. */}
                      <Button variant="primary" block disabled={!everyoneIn} onClick={() => setConfirming("sanction")}>
                        Sanction this loan
                      </Button>
                      <p className="sf-muted" style={{ margin: 0 }}>
                        {everyoneIn
                          ? "Every mandatory approver has approved it. Sanctioning makes the loan."
                          : waiting.length
                          ? `Cannot be sanctioned yet. Still to approve: ${waiting.join(", ")}.`
                          : "Cannot be sanctioned yet: the mandatory approvers have not all approved."}
                      </p>
                    </>
                  ) : null}
                  {mayDelete ? (
                    <Button variant="danger" block onClick={() => setConfirming("remove")}>
                      Delete this application
                    </Button>
                  ) : null}
                </>
              ) : null}
            </div>

            {confirming ? (
              <Confirm
                title={
                  {
                    approve: "Approve this application?",
                    unapprove: "Withdraw your approval?",
                    sanction: "Sanction this loan?",
                    remove: "Delete this application?",
                  }[confirming]
                }
                confirmLabel={{ approve: "Approve", unapprove: "Withdraw", sanction: "Sanction", remove: "Delete" }[confirming]}
                danger={confirming === "remove"}
                busy={busy}
                onCancel={() => setConfirming(null)}
                onConfirm={() => act(confirming)}
              >
                {
                  {
                    approve: `You are saying you are happy for the fund to lend ${money(form.amount)} to ${memberName(form) || "this member"}.`,
                    unapprove: "The application will no longer count your approval.",
                    sanction: `This sanctions ${money(form.amount)} to ${memberName(form) || "this member"} and makes the loan.`,
                    remove: `The ${money(form.amount)} application from ${memberName(form) || "this member"} will be removed.`,
                  }[confirming]
                }
              </Confirm>
            ) : null}
          </Page>
        );
      }}
    </Async>
  );
};

// ---------------------------------------------------------------- account

/**
 * Delete my account, from inside the console — Google Play's rule for the app
 * is that it can be done where the account is used. Asks for the password
 * again: an open browser must not be enough, and it cannot be undone.
 */
export const DeleteAccountPage = () => {
  const navigate = useNavigate();
  const { setAuth, setOrganisation } = useAuth();
  const [password, setPassword] = useState("");
  const [understood, setUnderstood] = useState(false);
  const { busy, run } = useAction();

  const remove = async () => {
    const ok = await run(() => axiosInstance.post(`/api/user/deleteaccount`, { password }), { done: "Your account has been deleted." });
    if (!ok) return;
    ["accessToken", "refreshToken", "user", "organisation"].forEach((key) => localStorage.removeItem(key));
    setAuth({});
    setOrganisation({});
    navigate("/signin", { replace: true });
  };

  return (
    <Page title="Delete account" back>
      <Card>
        <p className="sf-row-title">What is deleted</p>
        <ul className="sf-muted" style={{ paddingLeft: "1.1rem" }}>
          <li style={{ display: "list-item", animation: "none", background: "none", width: "auto", height: "auto" }}>Your email and password — you will not be able to sign in again</li>
          <li style={{ display: "list-item", animation: "none", background: "none", width: "auto", height: "auto" }}>Your phone number, address, age, gender and photo</li>
          <li style={{ display: "list-item", animation: "none", background: "none", width: "auto", height: "auto" }}>Your bank details and UPI ID</li>
          <li style={{ display: "list-item", animation: "none", background: "none", width: "auto", height: "auto" }}>Your membership of every fund, on every device</li>
        </ul>
        <p className="sf-row-title">What your fund keeps</p>
        <p className="sf-muted">
          Your name, member ID, and the fund's records of your savings, loans and repayments. They are part of the fund's accounts, which every
          other member's balances depend on. If you still owe the fund money, deleting your account does not cancel it.
        </p>
      </Card>
      <Card>
        <Field label="Password">
          <input type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} />
        </Field>
        <label className="sf-check">
          <input type="checkbox" checked={understood} onChange={(event) => setUnderstood(event.target.checked)} />I understand my account will be deleted
          permanently.
        </label>
        <div style={{ marginTop: "1rem" }}>
          <Button variant="danger" block busy={busy} disabled={!understood || !password} onClick={remove}>
            Delete my account
          </Button>
        </div>
      </Card>
    </Page>
  );
};
